import moment from 'moment'
import { CompetitionData, IncentiveData } from 'redux/employee/employeeTypes'

export interface EngagementSegment {
  name: string
  archived?: boolean
}

export interface EngagementReward {
  id: string
  createdAt: string
  employeeId: string
  amount: number
}

export interface EngagementRow {
  id: string
  title: string
  amountCents: number
  caption?: string
}

const formatPosition = (position: number) => {
  if (position === 1) {
    return '1st'
  }
  if (position === 2) {
    return '2nd'
  }
  return '3rd'
}

const monthStatus = (status: string, startDate: string, currentDate: string) => {
  const startMonth = moment(startDate, 'YYYY-MM-DD').format('YYYY-MM')
  const monthDiff = moment(currentDate).diff(startMonth, 'month')
  if (monthDiff >= 0) {
    return status
  }
  if (monthDiff === -1) {
    return 'PENDING'
  }
  return 'DISABLED'
}

const worksiteLabel = (name: string) => {
  if (!name.includes('Worksite')) {
    return name
  }
  return name.split(',')[0].replace('Worksite ', '') || name
}

export const commuterGroupNames = (
  segmentsEnrolled: string[] | undefined,
  segmentsById: Record<string, EngagementSegment> | null | undefined
) => {
  const names: string[] = []
  ;(segmentsEnrolled || []).forEach((id) => {
    const segment = segmentsById?.[id]
    if (!segment || segment.archived || segment.name === 'All Employees') {
      return
    }
    names.push(worksiteLabel(segment.name))
  })
  return names
}

export const oneTimeRewardsEarned = (
  rewards: EngagementReward[] | null | undefined,
  employeeId: string,
  currentDate: string
) =>
  (rewards || [])
    .filter(
      (reward) =>
        reward.employeeId === employeeId &&
        moment(reward.createdAt).format('YYYY-MM') === currentDate
    )
    .reduce((total, reward) => total + (reward.amount || 0), 0)

export const buildIncentiveRows = (
  incentives: IncentiveData[] | undefined,
  segmentsById: Record<string, EngagementSegment> | null | undefined,
  currentDate: string,
  oneTimeRewardCents: number
): EngagementRow[] => {
  const rows: EngagementRow[] = []
  ;(incentives || []).forEach((incentive) => {
    const status = monthStatus(incentive.status, incentive.startDate, currentDate)
    if (status === 'DISABLED') {
      return
    }
    rows.push({
      id: incentive.id,
      title: worksiteLabel(
        segmentsById?.[incentive.segmentId]?.name || 'Incentive'
      ),
      amountCents: status === 'ACTIVE' ? incentive.totalRewarded || 0 : 0,
      caption: status === 'ACTIVE' ? undefined : 'Pending',
    })
  })
  rows.push({
    id: 'one-time-rewards',
    title: 'One Time Rewards',
    amountCents: oneTimeRewardCents,
  })
  return rows
}

const competitionCaption = (
  winningPosition: number,
  isCurrentMonth: boolean,
  status: string
) => {
  if (status === 'PENDING' || status === 'DISABLED') {
    return 'Pending'
  }
  if (isCurrentMonth) {
    return 'Competing'
  }
  if (winningPosition) {
    return formatPosition(winningPosition)
  }
  return "Didn't win"
}

const competitionAmount = (
  winningPosition: number,
  winningReward: number,
  isCurrentMonth: boolean,
  status: string
) => {
  if (
    status !== 'PENDING' &&
    status !== 'DISABLED' &&
    !isCurrentMonth &&
    winningPosition
  ) {
    return winningReward || 0
  }
  return 0
}

export const buildCompetitionRows = (
  competitions: CompetitionData[] | undefined,
  currentDate: string,
  titleFor: (queryType: string) => string
): EngagementRow[] => {
  const isCurrentMonth = moment(currentDate).isSame(new Date(), 'month')
  const rows: EngagementRow[] = []
  ;(competitions || []).forEach((competition) => {
    const status = monthStatus(
      competition.status,
      competition.startDate,
      currentDate
    )
    if (status === 'DISABLED') {
      return
    }
    rows.push({
      id: competition.id,
      title: titleFor(competition.queryType),
      caption: competitionCaption(
        competition.winningPosition,
        isCurrentMonth,
        status
      ),
      amountCents: competitionAmount(
        competition.winningPosition,
        competition.winningReward,
        isCurrentMonth,
        status
      ),
    })
  })
  return rows
}
